import React, { useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { useStaffAuth } from '../../contexts/StaffAuthContext'
import { controlCenterApi, type AccountDetail } from '../../services/controlCenterApi'
import { Modal } from '../shared/Modal'

interface AccountContext {
  account: AccountDetail
  refreshAccount: () => void
}

export function AccountOverviewTab(): React.ReactElement {
  const { account, refreshAccount } = useOutletContext<AccountContext>()
  const { staff } = useStaffAuth()
  const isSuperStaff = staff?.level === 'super_staff'

  const [reactivating, setReactivating] = useState(false)
  const [reactivateError, setReactivateError] = useState<string | null>(null)
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [activating, setActivating] = useState(false)
  const [activateError, setActivateError] = useState<string | null>(null)
  const [showBillingHoldModal, setShowBillingHoldModal] = useState(false)
  const [togglingBillingHold, setTogglingBillingHold] = useState(false)
  const [billingHoldError, setBillingHoldError] = useState<string | null>(null)

  const isInactive = !account.active || account.deleted_at !== null

  const handleReactivate = async () => {
    setReactivating(true)
    setReactivateError(null)
    try {
      await controlCenterApi.reactivateAccount(account.id)
      refreshAccount()
    } catch (err) {
      setReactivateError(err instanceof Error ? err.message : 'Failed to reactivate')
    } finally {
      setReactivating(false)
    }
  }

  const handleActivate = async () => {
    setActivating(true)
    setActivateError(null)
    try {
      await controlCenterApi.activateAccount(account.id)
      refreshAccount()
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : 'Failed to activate account')
    } finally {
      setActivating(false)
    }
  }

  const handleDisableBillingHold = async () => {
    setTogglingBillingHold(true)
    setBillingHoldError(null)
    try {
      await controlCenterApi.toggleBillingHold(account.id, false)
      refreshAccount()
    } catch (err) {
      setBillingHoldError(err instanceof Error ? err.message : 'Failed to update billing hold')
    } finally {
      setTogglingBillingHold(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Billing Hold Banner */}
      {account.is_on_billing_hold && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-red-300 font-semibold text-sm">Billing Hold Active</h3>
              <p className="text-red-200/70 text-xs mt-0.5">
                This account is on billing hold. A black overlay is displayed on the dealer website.
              </p>
            </div>
          </div>
          <button
            onClick={handleDisableBillingHold}
            disabled={togglingBillingHold}
            className="bg-red-500 hover:bg-red-400 disabled:bg-red-700 text-white font-semibold rounded-lg px-5 py-2 text-sm transition-colors shrink-0"
          >
            {togglingBillingHold ? 'Removing...' : 'Remove Hold'}
          </button>
        </div>
      )}

      {/* Reactivation Banner */}
      {isInactive && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 flex items-center justify-between">
          <div>
            <h3 className="text-amber-300 font-semibold text-sm">Account is {account.deleted_at ? 'Deleted' : 'Inactive'}</h3>
            <p className="text-amber-200/70 text-xs mt-1">
              This account is not currently active. Reactivate to restore access for all users.
            </p>
            {reactivateError && (
              <p className="text-red-400 text-xs mt-1">{reactivateError}</p>
            )}
          </div>
          <button
            onClick={handleReactivate}
            disabled={reactivating}
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-amber-600 text-slate-900 font-semibold rounded-lg px-5 py-2 text-sm transition-colors shrink-0"
          >
            {reactivating ? 'Reactivating...' : 'Reactivate Account'}
          </button>
        </div>
      )}

      {/* Staff Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Billing Hold Toggle - any staff */}
        {account.active && !account.is_on_billing_hold && (
          <button
            onClick={() => setShowBillingHoldModal(true)}
            className="inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 font-medium rounded-lg px-4 py-2 text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Enable Billing Hold
          </button>
        )}
        {billingHoldError && (
          <span className="text-red-400 text-xs">{billingHoldError}</span>
        )}

        {/* Super Staff Actions */}
        {isSuperStaff && account.active && (
          <button
            onClick={() => setShowDeactivateModal(true)}
            className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-medium rounded-lg px-4 py-2 text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            Deactivate Account
          </button>
        )}
        {isSuperStaff && !account.active && (
          <>
            <button
              onClick={handleActivate}
              disabled={activating}
              className="inline-flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 font-medium rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {activating ? 'Activating...' : 'Activate Account'}
            </button>
            {activateError && (
              <span className="text-red-400 text-xs">{activateError}</span>
            )}
          </>
        )}
      </div>

      {/* Deactivation Details */}
      {account.deactivated_at && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-3">Deactivation Details</h3>
          <dl className="space-y-2">
            <InfoRow label="Deactivated At" value={new Date(account.deactivated_at).toLocaleString()} />
            {account.deactivation_reason && (
              <InfoRow label="Reason" value={account.deactivation_reason} />
            )}
          </dl>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Basic Info */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Basic Info</h2>
        <dl className="space-y-3">
          <InfoRow label="Name" value={account.name} />
          <InfoRow label="Email" value={account.email} />
          <InfoRow label="Phone" value={account.phone || '-'} />
          <InfoRow label="Domain" value={account.domain || '-'} />
          <InfoRow label="Type" value={account.account_type === 'group' ? 'Group' : 'Rooftop'} />
        </dl>
      </div>

      {/* Key Metrics */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Key Metrics</h2>
        <div className="grid grid-cols-2 gap-4">
          <MetricCard label="Users" value={account.users_count} />
          <MetricCard label="Child Accounts" value={account.children.length} />
        </div>
      </div>

      {/* Hierarchy */}
      {(account.parent_name || account.children.length > 0) && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Account Hierarchy</h2>
          {account.parent_name && (
            <div className="mb-4">
              <span className="text-xs text-slate-500 uppercase">Parent Group</span>
              <Link
                to={`/control_center/accounts/${account.parent_id}`}
                className="block text-sm text-white hover:text-primary transition-colors mt-1"
              >
                {account.parent_name}
              </Link>
            </div>
          )}
          {account.children.length > 0 && (
            <div>
              <span className="text-xs text-slate-500 uppercase">Child Accounts ({account.children.length})</span>
              <ul className="mt-2 space-y-1">
                {account.children.map(child => (
                  <li key={child.id}>
                    <Link
                      to={`/control_center/accounts/${child.id}`}
                      className="flex items-center gap-2 text-sm text-white hover:text-primary transition-colors py-1"
                    >
                      {child.name}
                      {!child.active && (
                        <span className="text-xs text-yellow-400">(Inactive)</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Timestamps */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Dates</h2>
        <dl className="space-y-3">
          <InfoRow label="Created" value={new Date(account.created_at).toLocaleString()} />
          <InfoRow label="Updated" value={new Date(account.updated_at).toLocaleString()} />
          {account.deleted_at && (
            <InfoRow label="Deleted" value={new Date(account.deleted_at).toLocaleString()} />
          )}
        </dl>
      </div>
    </div>

    {/* Deactivation Modal */}
    {showDeactivateModal && (
      <DeactivateModal
        accountName={account.name}
        accountId={account.id}
        onClose={() => setShowDeactivateModal(false)}
        onSuccess={refreshAccount}
      />
    )}

    {/* Billing Hold Confirmation Modal */}
    {showBillingHoldModal && (
      <BillingHoldModal
        accountName={account.name}
        accountId={account.id}
        onClose={() => setShowBillingHoldModal(false)}
        onSuccess={refreshAccount}
      />
    )}
  </div>
  )
}

function DeactivateModal({
  accountName,
  accountId,
  onClose,
  onSuccess,
}: {
  accountName: string
  accountId: string
  onClose: () => void
  onSuccess: () => void
}): React.ReactElement {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) return

    setSubmitting(true)
    setError(null)
    try {
      await controlCenterApi.deactivateAccount(accountId, reason.trim())
      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate account')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={true} onClose={onClose} size="md" theme="dark" variant="danger">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div>
            <div className="font-semibold">Deactivate Account</div>
            <div className="text-sm text-slate-400 font-normal">This will disable access for all users</div>
          </div>
        </span>
      </Modal.Header>

      <Modal.Body>
        <p className="text-sm text-slate-300 mb-4">
          You are about to deactivate <strong className="text-white">{accountName}</strong>. All users will lose access immediately.
        </p>

        <form id="deactivate-form" onSubmit={handleSubmit}>
          <label htmlFor="deactivation-reason" className="block text-sm font-medium text-slate-300 mb-1.5">
            Reason for deactivation <span className="text-red-400">*</span>
          </label>
          <textarea
            id="deactivation-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Enter the reason for deactivating this account..."
            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none"
            autoFocus
          />

          {error && (
            <p className="text-red-400 text-xs mt-2">{error}</p>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="deactivate-form"
          disabled={submitting || !reason.trim()}
          className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 disabled:bg-red-800 disabled:text-red-300 rounded-lg transition-colors"
        >
          {submitting ? 'Deactivating...' : 'Deactivate'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

function BillingHoldModal({
  accountName,
  accountId,
  onClose,
  onSuccess,
}: {
  accountName: string
  accountId: string
  onClose: () => void
  onSuccess: () => void
}): React.ReactElement {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await controlCenterApi.toggleBillingHold(accountId, true)
      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable billing hold')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={true} onClose={onClose} size="md" theme="dark" variant="warning">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <div className="font-semibold">Enable Billing Hold</div>
            <div className="text-sm text-slate-400 font-normal">This will display an overlay on the dealer website</div>
          </div>
        </span>
      </Modal.Header>

      <Modal.Body>
        <p className="text-sm text-slate-300 mb-4">
          You are about to place <strong className="text-white">{accountName}</strong> on billing hold.
          A black overlay will be displayed on the dealer&apos;s website until the hold is removed.
        </p>

        {error && (
          <p className="text-red-400 text-xs">{error}</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 rounded-lg transition-colors"
        >
          {submitting ? 'Enabling...' : 'Enable Hold'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

function InfoRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex justify-between items-center">
      <dt className="text-sm text-slate-400">{label}</dt>
      <dd className="text-sm text-white">{value}</dd>
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: number }): React.ReactElement {
  return (
    <div className="bg-slate-700/30 rounded-lg p-3 text-center">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  )
}
